/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client'

import { useTheme } from '@mui/material/styles'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
// TYPE-ONLY, and it has to stay that way: `site-nav.ts` reaches
// `@aglyn/aglyn/server`, and a value import here would drag the server entry
// into this `'use client'` module's graph. The import is erased at compile
// time, so the shape is shared without the dependency being real.
import type { SiteNavLink } from '../../utils/site-nav'

/**
 * The site's own mark, readable by anything under `[host]/[scheme]/layout` (AGL-2074).
 *
 * The layout already resolves the host's logo and display name to brand the
 * navigation loader; this publishes the same two values so the error
 * boundaries can wear them too. It exists because of a constraint of the App
 * Router rather than a design preference: **`not-found.tsx` and `error.tsx`
 * receive no `params`**, so neither can resolve the tenant host itself and
 * neither can call `getHostCached`. The layout is the last place in the tree
 * that knows which site this is, so what the boundaries need has to travel
 * down from there.
 *
 * Doing it as context rather than a server fetch inside the boundary is also
 * what keeps the 404 path free: the catch-all page is ISR (`revalidate = 60`)
 * and reading request state in a boundary rendered in the same pass would
 * force it dynamic, trading a cached 404 for a Firestore round trip on every
 * missing URL — which is exactly the traffic shape you least want to pay for.
 *
 * Both fields are optional on purpose. A host that set no logo, a lookup that
 * failed, or the root-level boundaries that render outside this provider all
 * land on the same answer — render the site's NAME, or nothing — instead of
 * substituting a platform mark. See `site-status-screen.component.tsx` for
 * why substituting one would be a white-label defect.
 */
export interface HostBrand {
  /** The site's display name, e.g. "Northwind Coffee". */
  brandName?: string
  /**
   * Resolved, site-relative logo URL; absent when the host set none.
   *
   * Read from the context, this is the logo for the ACTIVE scheme: the
   * provider swaps in {@link brandLogoDarkUrl} under a dark theme (AGL-3400),
   * so a reader renders one field and never has to ask which scheme is live.
   */
  brandLogoUrl?: string
  /**
   * The dark-scheme variant the site set, resolved like `brandLogoUrl`.
   * Absent means the one logo serves both schemes.
   */
  brandLogoDarkUrl?: string
  /**
   * The site's public top-level pages (AGL-2187) — the closest thing to a
   * navigation a boundary can render, and it travels the same way and for the
   * same reason as the two fields above: the layout is the last place in the
   * tree that knows which site this is.
   *
   * Absent, or empty, means the site has no other public top-level page (or
   * the sweep degraded). Both render as no nav at all rather than as an empty
   * bar — see `get-site-nav.ts` on why this may never fail loudly.
   *
   * Filtered to PUBLIC screens by `buildSiteNavLinks`. Anything that widens
   * what lands here is publishing page addresses to every visitor who mistypes
   * a URL; read that function's docstring before changing where this comes
   * from.
   */
  siteLinks?: SiteNavLink[]
  /**
   * The `[host]` route param for this request — `cname--example.com`,
   * `acme` — as the tenant API routes spell it (AGL-2342).
   *
   * Published for ONE reader: the not-found boundary, which has no `params` of
   * its own (Next does not pass them to `not-found.tsx`) and needs to name the
   * host to ask `/api/screen/not-found` for its designed 404 body. It is not
   * host DATA and nothing should render it — the brand fields above are what a
   * screen shows.
   *
   * Not a leak: this value is already in the flight payload of every tenant
   * response as the segment key, and already crosses to the client as
   * `AdminBarSlot`'s `host` prop.
   */
  hostKey?: string
  /**
   * The site's title as `buildMetadata` composes it — `seo.title`, else the
   * display name — and the host's title separator (AGL-2648).
   *
   * Published for the same single reader as `hostKey`: the not-found
   * boundary's client half writes `document.title` on a client-side
   * navigation to a missing URL, and it has to compose the SAME string the
   * server put in the `<head>` of a full document load, or the tab flips a
   * moment after hydration. `brandName` is not enough for that — the served
   * title joins the site's SEO title, which is what every routed page's does.
   * See `utils/not-found-title.ts` for the one rule both halves share.
   */
  siteTitle?: string
  titleSeparator?: string
  /**
   * The site's language, as its `<html lang>` names it (AGL-3771): what the
   * status pages' own words — the 404's, the error screen's — are written in.
   */
  language?: string
}

const HostBrandContext = createContext<HostBrand>({})

export function HostBrandProvider({
  brandName,
  brandLogoUrl: lightLogoUrl,
  brandLogoDarkUrl,
  siteLinks,
  hostKey,
  siteTitle,
  titleSeparator,
  language,
  children,
}: HostBrand & { children: ReactNode }) {
  // The theme, not the route's scheme segment: the two agree on the server
  // render, and only the theme follows a visitor who flips the scheme in the
  // page afterwards.
  const dark = useTheme().palette.mode === 'dark'
  const brandLogoUrl = (dark && brandLogoDarkUrl) || lightLogoUrl
  const value = useMemo(
    () => ({
      brandName,
      brandLogoUrl,
      brandLogoDarkUrl,
      siteLinks,
      hostKey,
      siteTitle,
      titleSeparator,
      language,
    }),
    [
      brandName,
      brandLogoUrl,
      brandLogoDarkUrl,
      siteLinks,
      hostKey,
      siteTitle,
      titleSeparator,
      language,
    ],
  )
  return (
    <HostBrandContext.Provider value={value}>
      {children}
    </HostBrandContext.Provider>
  )
}
HostBrandProvider.displayName = 'HostBrandProvider'

/**
 * The current site's brand. Returns an EMPTY object outside the provider
 * rather than throwing — the root-level boundaries in `app/` render above
 * `[host]/[scheme]/layout` by definition, and a boundary that throws while reporting
 * an error is the one failure mode none of this may have.
 */
export function useHostBrand(): HostBrand {
  return useContext(HostBrandContext)
}
